import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/UI/Buttons/Button/Button";
import AssociationLogoutButton from "../../components/AssociationLogoutButton/AssociationLogoutButton";
import AssociationSwitcher from "../../components/AssociationSwitcher/AssociationSwitcher";
import { selectTokenUserId } from "../../store/auth-slice";
import {
  selectAssociationContextState,
  selectSelectedAssociation,
} from "../../store/associationContext-slice";
import {
  AssociationsFetchResult,
  fetchMyAssociations,
} from "../../store/associationContext-actions";
import { ASSOCIATION_NOT_ADMINISTERED_ERROR } from "../../utils/associationErrors";
import DataCard from "./DataCard";
import PublicationCard from "./PublicationCard";
import MediaCard from "./MediaCard";
import HoursCard from "./HoursCard";
import DangerZoneCard from "./DangerZoneCard";
import styles from "./AssociationProfilePage.module.scss";

// Association portal, `/association`: the administered association. Slice 1
// is the shell: load states, the empty state, and the header (name, badge,
// advisor count, switcher). The cards mount below the header in this order:
// data form, publication, media, opening hours, danger zone.
//
// The page owns what spans the cards: one pending lock over every card's
// write (data, publication, media, opening hours, delete), so two of them
// never race on the same row and delete waits for any save; the
// unsaved-edits confirm before a switch (data form, picked or removed media,
// hours draft); the page-level notices that must outlive a card (a delete, a
// lost administrator pairing); and the light 30 s re-read of
// `/association/mine` while the page is visible, which updates the context
// only and never rebases an editor.

export const LOAD_ERROR_COPY = "Učitavanje nije uspelo. Pokušajte ponovo.";
export const REFRESH_ERROR_COPY =
  "Osvežavanje nije uspelo. Prikazani su poslednji učitani podaci.";
export const NO_ASSOCIATION_TITLE = "Nemate udruženja";
export const NO_ASSOCIATION_COPY =
  "Vaš nalog trenutno ne upravlja nijednim udruženjem. Pristup dodeljuje administrator.";
export const SWITCH_CONFIRM_COPY =
  "Imate nesačuvane izmene. Promeniti udruženje?";
export const ASSOCIATION_DELETED_NOTICE = "Udruženje je obrisano.";

export const STATUS_REFETCH_MS = 30000;

type PendingKey = "data" | "publication" | "media" | "hours" | "delete";

const NOTHING_PENDING: Record<PendingKey, boolean> = {
  data: false,
  publication: false,
  media: false,
  hours: false,
  delete: false,
};

type DirtyKey = "data" | "media" | "hours";

interface PageMessage {
  tone: "success" | "error";
  text: string;
}

const AssociationProfilePage: React.FC = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectTokenUserId);
  const { status, loadError } = useSelector(selectAssociationContextState);
  const association = useSelector(selectSelectedAssociation);

  const [pending, setPending] =
    useState<Record<PendingKey, boolean>>(NOTHING_PENDING);
  const [pageMessage, setPageMessage] = useState<PageMessage | null>(null);
  const dirtyRef = useRef<Record<DirtyKey, boolean>>({
    data: false,
    media: false,
    hours: false,
  });

  const busy = Object.values(pending).some(Boolean);

  const refetch = useCallback(async (): Promise<AssociationsFetchResult> => {
    if (userId == null) {
      return { ok: false };
    }
    return dispatch<any>(
      fetchMyAssociations(userId),
    ) as Promise<AssociationsFetchResult>;
  }, [dispatch, userId]);

  const retry = () => {
    void refetch();
  };

  const markPending = useCallback((key: PendingKey, value: boolean) => {
    if (value) {
      // A new write starts: the last page-level outcome is history.
      setPageMessage(null);
    }
    setPending((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
  }, []);
  const onDataPending = useCallback(
    (value: boolean) => markPending("data", value),
    [markPending],
  );
  const onPublicationPending = useCallback(
    (value: boolean) => markPending("publication", value),
    [markPending],
  );
  const onMediaPending = useCallback(
    (value: boolean) => markPending("media", value),
    [markPending],
  );
  const onHoursPending = useCallback(
    (value: boolean) => markPending("hours", value),
    [markPending],
  );
  const onDeletePending = useCallback(
    (value: boolean) => markPending("delete", value),
    [markPending],
  );

  const onDataDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current.data = dirty;
  }, []);
  const onMediaDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current.media = dirty;
  }, []);
  const onHoursDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current.hours = dirty;
  }, []);

  // ASSOCIATION_NOT_ADMINISTERED from any card: the copy lives here because
  // the re-read usually removes the association, and the card with it.
  const onNotAdministered = useCallback(() => {
    setPageMessage({ tone: "error", text: ASSOCIATION_NOT_ADMINISTERED_ERROR });
    void refetch();
  }, [refetch]);

  const onDeleted = useCallback(() => {
    setPageMessage({ tone: "success", text: ASSOCIATION_DELETED_NOTICE });
  }, []);

  const onCardSaved = useCallback(() => {
    void refetch();
  }, [refetch]);

  // The switcher's hook. Synchronous by contract (it returns whether to
  // proceed and does not say which association was picked), hence the
  // native confirm rather than a dialog.
  const confirmSwitch = useCallback((): boolean => {
    const dirty = Object.values(dirtyRef.current).some(Boolean);
    if (dirty && !window.confirm(SWITCH_CONFIRM_COPY)) {
      return false;
    }
    setPageMessage(null);
    return true;
  }, []);

  // A light 30 s re-read while the page is visible, no global polling: it
  // picks up `advisorCount` and ADMIN-side changes (including a first
  // association granted to an empty account) without a reload. It only
  // updates the context; the editors rebase on their own saves alone.
  const loaded = status === "loaded";
  useEffect(() => {
    if (userId == null || !loaded) {
      return;
    }
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        dispatch<any>(fetchMyAssociations(userId));
      }
    }, STATUS_REFETCH_MS);
    return () => clearInterval(interval);
  }, [dispatch, userId, loaded]);

  const renderPageMessage = () =>
    pageMessage ? (
      <p
        role={pageMessage.tone === "error" ? "alert" : "status"}
        className={
          pageMessage.tone === "error" ? styles.pageError : styles.pageSuccess
        }
      >
        {pageMessage.text}
      </p>
    ) : null;

  if (status === "idle" || status === "loading") {
    return (
      <div className={styles.page}>
        <p className={styles.placeholder}>Učitavanje udruženja...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p role="alert" className={styles.error}>
            {LOAD_ERROR_COPY}
          </p>
          <Button buttonType="blue" onClick={retry}>
            Pokušaj ponovo
          </Button>
        </div>
      </div>
    );
  }

  if (association == null) {
    return (
      <div className={styles.page}>
        {renderPageMessage()}
        <div className={styles.card}>
          <h1 className={styles.pageTitle}>{NO_ASSOCIATION_TITLE}</h1>
          <p className={styles.copy}>{NO_ASSOCIATION_COPY}</p>
          <AssociationLogoutButton />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.pageTitle}>{association.name}</h1>
          <div className={styles.meta}>
            {association.isNew && (
              <span className={styles.badge}>Novo udruženje</span>
            )}
            {!association.active && (
              <span className={styles.unpublished}>Nije objavljeno</span>
            )}
            <span className={styles.count}>
              Broj savetnika: {association.advisorCount ?? 0}
            </span>
          </div>
        </div>
        <AssociationSwitcher onBeforeChange={confirmSwitch} disabled={busy} />
      </header>
      {loadError && (
        <p role="alert" className={styles.notice}>
          {REFRESH_ERROR_COPY}
        </p>
      )}
      {renderPageMessage()}
      {/* Keyed on the association: a switch remounts the editors with the
          new association's values and discards the previous one's edits
          (after the confirm above). */}
      <DataCard
        key={`data-${association.id}`}
        association={association}
        busy={busy}
        onPendingChange={onDataPending}
        onDirtyChange={onDataDirtyChange}
        onNotAdministered={onNotAdministered}
        refetch={refetch}
      />
      <PublicationCard
        key={`publication-${association.id}`}
        association={association}
        busy={busy}
        onPendingChange={onPublicationPending}
        onNotAdministered={onNotAdministered}
        refetch={refetch}
      />
      {/* Not keyed: these two re-seed on an association change themselves. */}
      <MediaCard
        association={association}
        onSaved={onCardSaved}
        onNotAdministered={onNotAdministered}
        onPendingChange={onMediaPending}
        onDirtyChange={onMediaDirtyChange}
        locked={busy}
      />
      <HoursCard
        association={association}
        onSaved={onCardSaved}
        onNotAdministered={onNotAdministered}
        onPendingChange={onHoursPending}
        onDirtyChange={onHoursDirtyChange}
        locked={busy}
      />
      <DangerZoneCard
        key={`danger-${association.id}`}
        association={association}
        busy={busy}
        onPendingChange={onDeletePending}
        onNotAdministered={onNotAdministered}
        onDeleted={onDeleted}
        refetch={refetch}
      />
    </div>
  );
};

export default AssociationProfilePage;
